import Decimal from 'decimal.js';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { getMaxStowedWeight } from './stowageFillUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoTemperature } from './cargoTemperatureUtils';
import { createCargoOperationChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoOperationChangeDataFactory';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { createStowageItemChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/StowageItemChangeDataFactory';
import { undoStowageForOtherUnloadingCargoOperationsAndTank } from './undoStowageUtils';
import { updateUnstowOperationsForCargo } from './autoUnstowUtils';

const logTransfer: boolean = false;

export const transferToTank = (
    stowagePlan: StowagePlan,
    berthCall: RotationBerthCall,
    sourceCargoContent: CargoContent,
    destinationCargoContent: CargoContent,
    sourceTank: Tank,
    destinationTank: Tank
): StowagePlanChange[] | null => {
    const cargoTemperature = getCargoTemperature(stowagePlan, sourceCargoContent.cargo);
    const quantityToTransfer = getMaxStowedWeight(
        sourceCargoContent.cargo,
        new Decimal(destinationTank.maxWeightAt98Percent),
        new Decimal(destinationTank.volumeAt98Percent),
        destinationCargoContent?.weight ?? new Decimal(0),
        sourceCargoContent.weightInTransit,
        cargoTemperature
    );

    if (quantityToTransfer.isZero()) {
        return null;
    }

    if (logTransfer)
        console.log('Transferring ', quantityToTransfer.toString(), ' from ', sourceTank.odfjellTankName, ' to ', destinationTank.odfjellTankName);

    const newTransferCargoOperation = createRotationCargoOperation(berthCall, sourceCargoContent, quantityToTransfer);
    berthCall.cargoOperations.push(newTransferCargoOperation);

    const negativeStowage = createStowage(sourceTank, newTransferCargoOperation, quantityToTransfer.negated());
    const positiveStowage = createStowage(destinationTank, newTransferCargoOperation, quantityToTransfer);
    stowagePlan.stowageItems.push(negativeStowage);
    stowagePlan.stowageItems.push(positiveStowage);

    const stowagePlanChanges: StowagePlanChange[] = [
        createStowagePlanChange(
            stowagePlan.stowagePlanGuid,
            newTransferCargoOperation.cargoOperationGuid,
            StowageChangeDataType.RotationCargoOperation,
            createCargoOperationChangeData(newTransferCargoOperation)
        ),
        createStowagePlanChange(
            stowagePlan.stowagePlanGuid,
            negativeStowage.stowageGuid,
            StowageChangeDataType.StowageItem,
            createStowageItemChangeData(negativeStowage)
        ),
        createStowagePlanChange(
            stowagePlan.stowagePlanGuid,
            positiveStowage.stowageGuid,
            StowageChangeDataType.StowageItem,
            createStowageItemChangeData(positiveStowage)
        ),
    ];

    // undo future unloading stowage operations in source tank
    const undoneStowagePlanChanges = undoStowageForOtherUnloadingCargoOperationsAndTank(stowagePlan, sourceCargoContent.cargoOperation, sourceTank);

    // auto unstow future unloading operations in destination tank
    const unstowStowagePlanChanges = updateUnstowOperationsForCargo(stowagePlan, newTransferCargoOperation);

    return stowagePlanChanges.concat(undoneStowagePlanChanges).concat(unstowStowagePlanChanges);
};

const createRotationCargoOperation = (
    berthCall: RotationBerthCall,
    sourceCargoContent: CargoContent,
    quantityToTransfer: Decimal
): RotationCargoOperation => {
    const newTransferCargoOperation: RotationCargoOperation = {
        // TODO: CRYPTO-IMPORT - crypto.randomUUID() is used without importing crypto module. Should add 'import { randomUUID } from 'crypto';' or use a proper UUID generator.
        cargoOperationGuid: crypto.randomUUID(),
        berthCallGuid: berthCall.berthCallGuid,
        berthCall: berthCall,
        portCallGuid: berthCall.portCallGuid,
        cargoGuid: sourceCargoContent.cargo.cargoGuid,
        cargo: sourceCargoContent.cargo,
        cargoOperationSequenceNumber: 0,
        billOfLadingWeight: null,
        operationType: 'Transfer',
        contractWeight: null,
        nominatedWeight: quantityToTransfer.toNumber(),
        dataSource: 'OrcaWeb',
    };
    return newTransferCargoOperation;
};

const createStowage = (tank: Tank, cargoOperation: RotationCargoOperation, weight: Decimal): Stowage => ({
    // TODO: CRYPTO-IMPORT - crypto.randomUUID() is used without importing crypto module. Should add 'import { randomUUID } from 'crypto';' or use a proper UUID generator.
    stowageGuid: crypto.randomUUID(),
    tankId: tank.tankId,
    tank: tank,
    cargoOperationGuid: cargoOperation.cargoOperationGuid,
    cargoOperation: cargoOperation,
    weight: weight,
});
